import { type HTMLAttributes, type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './typography.css';

type TypographyVariant =
	| 'heading'
	| 'subheading'
	| 'eyebrow'
	| 'body'
	| 'footer'
	| 'error';

type TypographyElement = 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';

interface TypographyProps extends HTMLAttributes<HTMLElement> {
	children?: ReactNode;
	variant?: TypographyVariant;
	as?: TypographyElement;
}

const VARIANT_DEFAULT_ELEMENT: Record<TypographyVariant, TypographyElement> = {
	heading: 'h1',
	subheading: 'h2',
	eyebrow: 'p',
	body: 'p',
	footer: 'p',
	// Error rendering is a live region, not a paragraph — `<div>` is the
	// semantically correct host for `role="alert"` and lets the accessible-
	// name algorithm pick up the text content reliably across UAs.
	error: 'div',
};

const Typography = ({
	children,
	variant = 'body',
	as,
	id,
	className,
	role,
	...rest
}: TypographyProps) => {
	const Element = as ?? VARIANT_DEFAULT_ELEMENT[variant];
	const resolvedRole = role ?? (variant === 'error' ? 'alert' : undefined);
	// Instance-unique default id (explicit `id` prop wins) — see BaseButton.
	const auto_id = useId();
	return (
		<Element
			{...rest}
			id={id ?? `Typography-${auto_id}`}
			role={resolvedRole}
			className={classlist('typography', `typography--${variant}`, className)}
		>
			{children}
		</Element>
	);
};

export type { TypographyElement, TypographyProps, TypographyVariant };
export { Typography };
